/**
 * Plain-English mapping of classifier reason codes for the Triage UI.
 *
 * Operator-facing copy. Don't surface raw codes (e.g. "low_classifier_confidence")
 * in the UI — they're jargon. Map them here, fall back to a humanised version
 * of the raw code if a new code appears we haven't translated yet.
 *
 * Verdicts come from services/publish_safety.py.
 */

// ─── Per-signal plain-English labels ────────────────────────────────────
// Map every classifier signal to a human label + what each value means.
// When a signal disagrees between passes, the triage UI surfaces the
// label + each pass's reasoning so the operator can adjudicate with
// concrete context, not abstract "the AI passes disagreed".
export const SIGNAL_COPY = {
  candid_unposed: {
    label: "Candid vs posed",
    yes: "Caught mid-action (people unaware/unposed)",
    no: "Posed or aware shot (subjects know they're being photographed)",
  },
  child_present: {
    label: "Children present",
    yes: "Children visible in the frame",
    no: "No children visible",
  },
  face_count_high: {
    label: "Many faces",
    yes: "Crowd shot with many identifiable people",
    no: "Few or no faces visible",
  },
  identifiable_faces_present: {
    label: "Identifiable faces",
    yes: "People are clearly identifiable",
    no: "No identifiable faces visible",
  },
  private_setting: {
    label: "Private setting",
    yes: "Private/non-public location (home, bathroom, etc.)",
    no: "Public or professional setting",
  },
  client_environment: {
    label: "Client environment",
    yes: "Recognisably a client engagement or workplace",
    no: "Not clearly a client/work setting",
  },
  subject_is_remi: {
    label: "Subject is Remi",
    yes: "Remi appears in the photo",
    no: "Remi does not appear",
  },
  third_party_branding_visible: {
    label: "Third-party branding",
    yes: "Logos/branding from another organisation visible",
    no: "No third-party branding visible",
  },
};

export function signalCopy(signalKey) {
  return SIGNAL_COPY[signalKey] || {
    label: signalKey.replace(/_/g, " "),
    yes: "Yes",
    no: "No",
  };
}

// ─── Structured triage actions ──────────────────────────────────────────
// What the operator can DO with a photo. Each option records a structured
// ground-truth label that future classifier retraining can use as input.
// Keep this list short — every extra option is friction. Add categories
// only when a real photo lands that needs them.
export const TRIAGE_ACTIONS = [
  {
    code: "looks_good",
    label: "Looks good — keep it",
    blurb: "Photo is on-brand, consented, ready for publication.",
    tone: "emerald",
    is_active: true,
  },
  {
    code: "drop_off_brand",
    label: "Drop — off-brand or low quality",
    blurb: "Bad lighting, awkward framing, doesn't represent the business well.",
    tone: "rose",
    is_active: false,
    notes_optional: true,
  },
  {
    code: "drop_confidential",
    label: "Drop — confidential content visible",
    blurb: "Whiteboards, screens, documents with sensitive information.",
    tone: "rose",
    is_active: false,
  },
  {
    code: "drop_no_consent",
    label: "Drop — subject didn't consent to publication",
    blurb: "People in this photo who haven't agreed to be in published material.",
    tone: "rose",
    is_active: false,
  },
  {
    code: "drop_third_party",
    label: "Drop — third-party branding or IP",
    blurb: "Other organisations' logos, slides, or branded materials are prominent.",
    tone: "rose",
    is_active: false,
  },
  {
    code: "wrong_pool",
    label: "Keep but move to different pool",
    blurb: "Photo is fine, but mis-categorised (e.g. should be backgrounds, not subjects).",
    tone: "amber",
    is_active: true,
    needs_pool_pick: true,
  },
  {
    code: "skip",
    label: "Not sure — skip for now",
    blurb: "Come back to this later. Doesn't change anything.",
    tone: "slate",
    is_skip: true,
  },
];


const REASON_COPY = {
  // Phase 1.4 publish_safety verdicts
  all_clear: {
    headline: "AI is confident this is safe",
    blurb: "Both passes agreed the photo is on-brand and publishable.",
  },
  low_classifier_confidence: {
    headline: "The AI passes disagreed",
    blurb:
      "Gemini and Claude looked at this photo and reached different conclusions. " +
      "We need your call on whether it stays in the active pool.",
  },
  consent_unknown: {
    headline: "Consent status unclear",
    blurb:
      "We couldn't tell from the folder or metadata whether the people in this " +
      "photo have consented to being used in published material.",
  },
  consent_unverified: {
    headline: "Consent not yet verified",
    blurb:
      "This folder hasn't been marked consent-verified. If everyone in this " +
      "photo agreed to be in published material, mark the folder verified.",
  },
  unsafe_content_detected: {
    headline: "Possibly sensitive content",
    blurb:
      "One of the AI passes flagged this as potentially unsafe. Look carefully " +
      "and drop it if you wouldn't want it on the brand feed.",
  },
  // Agreement codes
  agreement_disagree: {
    headline: "The two AI passes disagreed",
    blurb:
      "Gemini said one thing, Claude said another. Your call is the tiebreaker.",
  },
  pass1_only: {
    headline: "Only one AI pass weighed in",
    blurb:
      "The second pass couldn't process this photo. The first pass's verdict " +
      "is what you're seeing.",
  },
  pass2_only: {
    headline: "Only one AI pass weighed in",
    blurb:
      "The first pass couldn't process this photo. The second pass's verdict " +
      "is what you're seeing.",
  },
};

/** Lookup with fallback — humanise unknown codes by stripping prefixes. */
export function reasonCopy(code) {
  if (!code) {
    return {
      headline: "No specific signal",
      blurb: "The classifier ran but didn't flag a specific concern.",
    };
  }
  if (REASON_COPY[code]) return REASON_COPY[code];
  // Fallback: pretty-print the raw code
  const pretty = String(code).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return {
    headline: pretty,
    blurb: `Internal signal code: ${code}. (Not yet translated.)`,
  };
}

const VERDICT_COPY = {
  safe: {
    headline: "Safe to publish",
    tone: "emerald",
  },
  ambiguous: {
    headline: "Needs review",
    tone: "amber",
  },
  unsafe: {
    headline: "Likely unsafe",
    tone: "rose",
  },
  unclassified: {
    headline: "Not yet reviewed by AI",
    tone: "slate",
  },
};

export function verdictCopy(verdict) {
  return VERDICT_COPY[verdict] || VERDICT_COPY.unclassified;
}
